/**
 * Quiz Player Page
 *
 * Full-screen quiz experience for operators. Loaded outside OperatorLayout
 * (no tab bar) at /operator/practice/quiz/:sectionId.
 *
 * Flow: loading -> playing (one question at a time) -> submitting -> results.
 *
 * Design decision: The API does NOT include correct answers in the quiz load
 * response (server-side grading). Immediate per-question correct/incorrect
 * feedback is therefore not possible. Instead, we show a brief "Answer recorded"
 * confirmation after each selection, auto-advance to the next question, and
 * reveal all correct/incorrect results on the results screen after submission.
 */

import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Brain, AlertTriangle, Loader2 } from 'lucide-react';
import { PlayerShell } from './components/PlayerShell';
import { QuizQuestion } from './components/QuizQuestion';
import { QuizResults } from './components/QuizResults';
import { OperatorLoadingSkeleton } from './components/OperatorLoadingSkeleton';
import { OperatorEmptyState } from './components/OperatorEmptyState';
import { useQuiz, useSubmitQuiz, usePracticeStats } from './hooks/useOperatorPractice';
import type { QuizQuestion as QuizQuestionType, QuizResultItem } from '@guild/shared';

/** Auto-advance delay after selection confirmation (milliseconds) */
const FEEDBACK_DELAY_MS = 1500;

type QuizState = 'loading' | 'playing' | 'submitting' | 'results';

interface AnswerRecord {
  questionId: string;
  questionType: 'multiple_choice' | 'true_false';
  selectedAnswer: string;
}

export function QuizPlayerPage() {
  const { sectionId } = useParams<{ sectionId: string }>();

  // --- Data fetching ---
  const { data: quiz, isLoading, error, refetch } = useQuiz(sectionId ?? null);
  const submitQuiz = useSubmitQuiz();
  const { data: stats } = usePracticeStats();

  // --- Local state ---
  const [quizState, setQuizState] = useState<QuizState>('loading');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Map<string, AnswerRecord>>(new Map());
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [submissionResults, setSubmissionResults] = useState<{
    score: number;
    correctAnswers: number;
    totalQuestions: number;
    results: QuizResultItem[];
  } | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const feedbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Refs for values needed in setTimeout callbacks (avoids stale closures)
  const currentIndexRef = useRef(currentIndex);
  currentIndexRef.current = currentIndex;
  const answersRef = useRef(answers);
  answersRef.current = answers;
  const elapsedSecondsRef = useRef(elapsedSeconds);
  elapsedSecondsRef.current = elapsedSeconds;

  // --- Timer management ---
  useEffect(() => {
    if (quizState === 'playing') {
      timerRef.current = setInterval(() => {
        setElapsedSeconds((prev) => prev + 1);
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [quizState]);

  // --- Initialize quiz on data load ---
  useEffect(() => {
    if (quiz && quizState === 'loading') {
      setQuizState('playing');
    }
  }, [quiz, quizState]);

  // --- Cleanup feedback timer on unmount ---
  useEffect(() => {
    return () => {
      if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
    };
  }, []);

  // --- Build question text map for results ---
  const questionTexts = useMemo(() => {
    if (!quiz) return new Map<string, string>();
    return new Map(quiz.questions.map((q) => [q.questionId, q.questionText]));
  }, [quiz]);

  // --- Current question ---
  const currentQuestion: QuizQuestionType | null =
    quiz && currentIndex < quiz.questions.length ? quiz.questions[currentIndex] : null;

  // --- Submit quiz (reads from refs to avoid stale closures) ---
  const doSubmit = useCallback(async () => {
    if (!quiz || !sectionId) return;

    setQuizState('submitting');

    // Stop the timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // Build responses array in question order (read from ref for latest state)
    const currentAnswers = answersRef.current;
    const currentElapsed = elapsedSecondsRef.current;
    const responses = quiz.questions.map((q) => {
      const answer = currentAnswers.get(q.questionId);
      return {
        questionId: q.questionId,
        questionType: (q.isTrueFalse ? 'true_false' : 'multiple_choice') as 'true_false' | 'multiple_choice',
        selectedAnswer: answer?.selectedAnswer ?? '',
      };
    });

    try {
      const result = await submitQuiz.mutateAsync({
        sectionId,
        responses,
        elapsedSeconds: currentElapsed,
      });

      setSubmissionResults(result);
      setQuizState('results');
      setSubmissionError(null);
    } catch (err) {
      console.error('Failed to submit quiz:', err);
      setSubmissionError('Failed to submit quiz. Please try again.');
      setQuizState('playing');
      // Restore to last question so user can retry submission
      setCurrentIndex(quiz.questions.length - 1);
    }
  }, [quiz, sectionId, submitQuiz]);

  // Keep a stable ref for doSubmit so setTimeout always has the latest version
  const doSubmitRef = useRef(doSubmit);
  doSubmitRef.current = doSubmit;

  // --- Handle answer selection ---
  const handleAnswer = useCallback(
    (answer: string) => {
      if (!currentQuestion || showFeedback) return;

      setSelectedAnswer(answer);

      // Record answer
      const record: AnswerRecord = {
        questionId: currentQuestion.questionId,
        questionType: currentQuestion.isTrueFalse ? 'true_false' : 'multiple_choice',
        selectedAnswer: answer,
      };
      setAnswers((prev) => {
        const next = new Map(prev);
        next.set(currentQuestion.questionId, record);
        return next;
      });

      // Show selection confirmation, then advance after delay
      setShowFeedback(true);

      feedbackTimerRef.current = setTimeout(() => {
        setShowFeedback(false);
        setSelectedAnswer(null);

        // Use ref to read latest currentIndex (avoids stale closure)
        const idx = currentIndexRef.current;
        if (quiz && idx + 1 < quiz.questions.length) {
          setCurrentIndex((prev) => prev + 1);
        } else {
          // Last question — submit quiz
          doSubmitRef.current();
        }
      }, FEEDBACK_DELAY_MS);
    },
    [currentQuestion, showFeedback, quiz],
  );

  // --- Try Again handler ---
  const handleTryAgain = useCallback(() => {
    // Reset all state
    setQuizState('loading');
    setCurrentIndex(0);
    setAnswers(new Map());
    setSelectedAnswer(null);
    setShowFeedback(false);
    setSubmissionResults(null);
    setElapsedSeconds(0);
    setSubmissionError(null);

    // Re-fetch quiz for new random selection
    refetch();
  }, [refetch]);

  // --- Leave confirmation ---
  useEffect(() => {
    if (quizState !== 'playing' || answers.size === 0) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [quizState, answers.size]);

  // ============================================
  // RENDER
  // ============================================

  // Loading state
  if (isLoading || (quizState === 'loading' && !error)) {
    return (
      <div className="min-h-screen bg-op-surface">
        <div className="mx-auto max-w-lg px-4 py-8">
          <div className="flex flex-col items-center gap-4 py-16">
            <Loader2 size={32} className="animate-spin text-guild-green" />
            <p className="text-sm font-medium text-op-text-secondary">
              Generating quiz...
            </p>
          </div>
          <OperatorLoadingSkeleton variant="cards" />
        </div>
      </div>
    );
  }

  // Error state
  if (error || !quiz) {
    return (
      <div className="min-h-screen bg-op-surface">
        <OperatorEmptyState
          icon={AlertTriangle}
          title="Quiz Not Available"
          description="This quiz could not be loaded. The section may not have enough questions yet."
          ctaLabel="Back to Content"
          ctaHref="/operator/content"
        />
      </div>
    );
  }

  // Results state
  if (quizState === 'results' && submissionResults) {
    return (
      <QuizResults
        correctAnswers={submissionResults.correctAnswers}
        totalQuestions={submissionResults.totalQuestions}
        score={submissionResults.score}
        elapsedSeconds={elapsedSeconds}
        bestScore={stats?.quizBestScore ?? null}
        results={submissionResults.results}
        questionTexts={questionTexts}
        onTryAgain={handleTryAgain}
        title={`${quiz.sopTitle} — ${quiz.sectionTitle}`}
        sectionId={sectionId}
      />
    );
  }

  // Submitting state
  if (quizState === 'submitting') {
    return (
      <div className="min-h-screen bg-op-surface">
        <div className="flex flex-col items-center justify-center py-32">
          <Loader2 size={32} className="animate-spin text-guild-green" />
          <p className="mt-4 text-sm font-medium text-op-text-secondary">
            Scoring your answers...
          </p>
        </div>
      </div>
    );
  }

  // Playing state
  if (!currentQuestion) {
    return null;
  }

  return (
    <PlayerShell
      title={`${quiz.sopTitle} — ${quiz.sectionTitle}`}
      subtitle="Knowledge Check"
      current={currentIndex + 1}
      total={quiz.totalQuestions}
      progressLabel="Q"
      inProgress={answers.size > 0}
      backTo="/operator/content"
      showTimer
      elapsedSeconds={elapsedSeconds}
    >
      {/* Submission error banner */}
      {submissionError && (
        <div className="mb-4 rounded-2xl bg-red-50 p-4 ring-1 ring-red-200">
          <p className="text-sm font-medium text-red-700">{submissionError}</p>
          <button
            type="button"
            onClick={doSubmit}
            className="mt-2 text-sm font-semibold text-red-700 underline"
          >
            Retry submission
          </button>
        </div>
      )}

      <QuizQuestion
        key={currentQuestion.questionId}
        questionText={currentQuestion.questionText}
        questionNumber={currentQuestion.questionNumber}
        isTrueFalse={currentQuestion.isTrueFalse}
        options={currentQuestion.options}
        selectedAnswer={selectedAnswer}
        showFeedback={showFeedback}
        onAnswer={handleAnswer}
      />

      {/* Selection confirmation (answers not graded until submission) */}
      {showFeedback && (
        <div
          className="mt-5 flex items-center justify-center gap-2"
          aria-live="polite"
          style={{ animation: 'fadeInUp 0.2s ease-out' }}
        >
          <div className="flex items-center gap-2 rounded-full bg-guild-green/10 px-4 py-2">
            <Brain size={16} strokeWidth={1.5} className="text-guild-green" />
            <span className="text-sm font-medium text-guild-green">
              {currentIndex + 1 < quiz.totalQuestions
                ? 'Answer recorded — next question...'
                : 'Answer recorded — scoring...'}
            </span>
          </div>
        </div>
      )}
    </PlayerShell>
  );
}
